import { isNil, isObject, isArray, isString, has, map, forEach, some, cloneDeep, uniq, reduce, transform, indexOf, remove } from 'lodash';
import { expresionResolvr } from '../common/helpers';
import { DISABLED_VALUE, SELF_DISABLED_VALUE, DISABLED_COLUMNS } from '../../constants';

class RulesMutatorApplicator
{
    constructor()
    {
        // @var Array -> Array -> Object
        this.rules = [];
    }

    /**
     * @param Object
     */
    addRule(rules)
    {

        if (isArray(rules))
        {
            if (rules.length === 1)
            {
                this.rules.push(rules);
            }
            else if (rules.length > 1)
            {
                const byTargetsAndTypes = reduce(rules, (accumulator, item) =>
                {
                    let key = `${item.target}.${item.type}`;

                    if (!isNil(item.param))
                    {
                        if (isObject(item.param))
                        {
                            if (has(item.param, 'col'))
                            {
                                key = `${key}.${item.param.col}`;
                            }
                            else if (has(item.param, 'row'))
                            {
                                key = `${key}.${item.param.row}`;
                            }
                            else
                            {
                                throw new Error('Rule definition not implemented yet!!!');
                            }
                        }
                        else
                        {
                            key = `${key}.${item.param}`;
                        }
                    }

                    if (!accumulator.hasOwnProperty(key))
                    {
                        accumulator[key] = [];
                    }
                    accumulator[key].push(item);
                    return accumulator;
                }, {});

                forEach(byTargetsAndTypes, (group) =>
                {
                    this.rules.push(group);
                });
            }
        }
    }

    /**
     * @param Object
     * @param string
     * @param string|null
     * @param string|null
     */
    applyAnswer(answer, value, row =null, col =null)
    {
        const cloned = cloneDeep(answer);
        if (!isNil(row))
        {
            if (!isNil(col))
            {
                return this.set3dimAnswer(cloned, value, row, col);
            }
            return this.set2dimAnswer(cloned, value, row);
        }
        return this.set1dimAnswer(cloned, value);
    }

    /**
     * @param Object
     * @return Array
     */
    applyRules(answers)
    {
        const mutatedAnswers = [];
        forEach(this.rules, (rule) =>
        {
            const [changed, uukey, mutatedAnswer] = this.applyRule(rule, answers);
            if (changed)
            {
                mutatedAnswers.push({
                    uukey,
                    mutatedAnswer,
                });
            }
        })
        return mutatedAnswers;
    }

    /**
     * @param Array|Object
     * @param Object
     * @return [boolean, string, object]
     */
    applyRule(rule, answers)
    {
        let changed = false;
        let uukey = null;
        let mutatedAnswer = null;
        let param;

        const orRules = (isArray(rule)) ? rule : [rule];

        // Make sure all orRules have the same target:
        const targets = uniq(map(orRules, (item) =>
        {
            return `${item.target}.${item.type}`;
        }));
        if (targets.length > 1)
        {
            throw new Error('Rule sets should only target the same answer with the same type. ' + targets);
        }

        const targetID = orRules[0].target;
        const target = answers[targetID];
        const ruleType = orRules[0].type;

        let scope;
        const positive = some(orRules, (orRule) =>
        {
            scope = answers[orRule.scope];
            if (isNil(scope))
            {
                return false;
            }
            if (scope.answer != DISABLED_VALUE)
            {
                return expresionResolvr(orRule.rules, scope.answer);
            }
            return false;
        });

        switch(ruleType)
        {
            case 'disable':
                if (positive && target.answer != DISABLED_VALUE)
                {
                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer = DISABLED_VALUE;
                    // mutatedAnswer.disabled = true;
                }
                else if (!positive && target.answer == DISABLED_VALUE)
                {
                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer = '';
                    // mutatedAnswer.disabled = false;
                }
            break;

            case 'disable_row':

                param = orRules[0].param;
                if (!has(param, 'row'))
                {
                    throw new Error('Missing parameter (param.row) for rule of type `disable_row` (AD)');
                }

                if (isString(target.answer) && (target.answer === DISABLED_VALUE || target.answer === SELF_DISABLED_VALUE))
                {
                    break;
                }

                if (!isObject(target.answer))
                {
                    target.answer = {};
                }
                if (!has(target.answer, param.row))
                {
                    target.answer[param.row] = '';
                }

                if (positive && target.answer[param.row] != DISABLED_VALUE)
                {
                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer[param.row] = DISABLED_VALUE;
                }
                else if (!positive && target.answer[param.row] == DISABLED_VALUE)
                {
                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer[param.row] = '';
                }
            break;

            case 'disable_col':
                if (target.answer == DISABLED_VALUE || target.answer == SELF_DISABLED_VALUE)
                {
                    break;
                }
                param = orRules[0].param;

                if (!has(param, 'col'))
                {
                    throw new Error('Missing parameter (param.col) for rule of type `disable_col` (AD)');
                }

                if (isString(target.answer) && (target.answer === DISABLED_VALUE || target.answer === SELF_DISABLED_VALUE))
                {
                    break;
                }

                if (!isObject(target.answer))
                {
                    target.answer = {
                        [DISABLED_COLUMNS]: [],
                    };
                }
                else if (!has(target.answer, DISABLED_COLUMNS))
                {
                    target.answer[DISABLED_COLUMNS] = [];
                }

                if (positive && indexOf(target.answer[DISABLED_COLUMNS], param.col) === -1)
                {
                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer[DISABLED_COLUMNS].push(param.col);
                    forEach(mutatedAnswer.answer, (row, rowID) =>
                    {
                        if (rowID === DISABLED_COLUMNS)
                        {
                            return;
                        }
                        if (!isObject(row))
                        {
                            mutatedAnswer.answer[rowID] = {
                                [param.col]: DISABLED_VALUE,
                            };
                            return;
                        }
                        row[param.col] = DISABLED_VALUE;
                    });
                }
                else if (!positive && indexOf(target.answer[DISABLED_COLUMNS], param.col) > -1)
                {


                    changed = true;
                    uukey = targetID;
                    mutatedAnswer = cloneDeep(target);
                    mutatedAnswer.answer[DISABLED_COLUMNS].splice(indexOf(mutatedAnswer.answer[DISABLED_COLUMNS], param.col), 1);
                    forEach(mutatedAnswer.answer, (row, rowID) =>
                    {
                        if (rowID === DISABLED_COLUMNS)
                        {
                            return;
                        }
                        if (!isObject(row))
                        {
                            mutatedAnswer.answer[rowID] = {
                                [param.col]: '',
                            };
                            return;
                        }
                        row[param.col] = '';
                    });
                }
            break;
        }

        return [changed, uukey, mutatedAnswer];
    }

    set1dimAnswer(cloned, value)
    {
        cloned.answer = value;
        return cloned;
    }

    set2dimAnswer(cloned, value, row)
    {
        if (!isObject(cloned.answer))
        {
            cloned.answer = {};
        }
        cloned.answer[row] = value;
        return cloned;
    }

    set3dimAnswer(cloned, value, row, col)
    {
        if (!isObject(cloned.answer))
        {
            cloned.answer = {};
        }
        if (!isObject(cloned.answer[row]))
        {
            cloned.answer[row] = {};
        }
        cloned.answer[row][col] = value;
        return cloned;
    }

}

const rulesMutatorApplicator = new RulesMutatorApplicator();

export { rulesMutatorApplicator }



// WEBPACK FOOTER //
// ./resources/assets/js/components/vuex/RulesMutatorApplicator.js